import React from "react";
import PropTypes from "prop-types";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
} from "@mui/material";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";

// Unsaved-changes Navigation Guard —
// covers actual React Router navigation (sidebar clicks via navigate(),
// <Link> clicks anywhere in the app, browser back/forward). This is the
// counterpart to the in-app tab-switch guard already built into
// DtrDetails.js — that one covers switching tabs (no URL change, a plain
// React state/unmount), this one covers leaving the page entirely (a real
// URL change), which is what useBlocker() is actually designed to guard.
//
// `blocker` is the object returned by useBlocker() in the calling screen —
// kept there (not here) since useBlocker must be called from the component
// whose navigation it's guarding, per React Router's rules of hooks.
export default function UnsavedChangesBlockerDialog({ blocker, entityLabel }) {
  if (!blocker || blocker.state !== "blocked") return null;

  return (
    <Dialog open onClose={() => blocker.reset()} maxWidth="xs" fullWidth>
      <DialogTitle
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1,
          color: "#d32f2f",
          fontFamily: "Urbanist, Arial, sans-serif",
          fontWeight: "bold",
        }}
      >
        <WarningAmberIcon color="error" />
        Unsaved Changes
      </DialogTitle>
      <DialogContent>
        <DialogContentText sx={{ fontFamily: "Urbanist, Arial, sans-serif" }}>
          {`You have unsaved ${entityLabel} changes (edited text, a pasted image, or an attached file) that haven't been confirmed with Save. Leaving this page now will discard the unsaved text and lose track of any new attachment. Leave anyway?`}
        </DialogContentText>
      </DialogContent>
      <DialogActions sx={{ padding: "16px 24px" }}>
        <Button
          variant="outlined"
          onClick={() => blocker.reset()}
          sx={{
            fontFamily: "Urbanist, Verdana, Arial, sans-serif",
            borderRadius: "8px",
            textTransform: "none",
            fontWeight: 600,
          }}
        >
          Stay on This Page
        </Button>
        <Button
          variant="contained"
          color="error"
          onClick={() => blocker.proceed()}
          sx={{
            fontFamily: "Urbanist, Verdana, Arial, sans-serif",
            borderRadius: "8px",
            textTransform: "none",
            fontWeight: 600,
          }}
        >
          Discard and Leave
        </Button>
      </DialogActions>
    </Dialog>
  );
}

UnsavedChangesBlockerDialog.propTypes = {
  blocker: PropTypes.shape({
    state: PropTypes.string,
    proceed: PropTypes.func,
    reset: PropTypes.func,
  }),
  entityLabel: PropTypes.string.isRequired,
};
